<template>
  <div>
    <!-- Email Validation -->
    <div v-if="stepState.method === 'email'">
      <h1 class="text-center">
        {{ updatingContact ? $t('enrollmentStep.contactVerification.updateYourEmail') : $t('enrollmentStep.contactVerification.verifyYourEmail') }}
      </h1>
      <!-- missing email || update contact -->
      <div v-if="!stepState.contact || updatingContact">
        <form @submit.prevent="updateContact('email')">
          <p v-if="!updatingContact" class="lead text-center">
            {{ $t('enrollmentStep.contactVerification.inOrderToVerify') }}
          </p>
          <div class="row text-center justify-content-center">
            <div class="col-md-6">
              <div class="form-group">
                <input
                  v-model="contactValue"
                  v-validate="'required|email'"
                  class="form-control form-control-lg"
                  name="email"
                  type="email"
                  autocomplete="off"
                >
              </div>
            </div>
          </div>
          <ul v-if="errors.has('email')" class="text-center text-danger list-unstyled">
            <li>{{ errors.first('email') }}</li>
          </ul>
          <div class="row text-center">
            <div class="col-md-12">
              <button class="btn btn-primary btn-lg" role="button" type="submit" :disabled="isSubmitting">
                {{ updatingContact && stepState.codeRequired ? $t('enrollmentStep.contactVerification.updateAndResend') : $t('enrollmentStep.contactVerification.updateEmail') }}
              </button>
            </div>
            <div v-if="updatingContact" class="col-md-12">
              <button class="btn btn-link" type="button" @click="cancelUpdate">
                {{ $t('cancel') }}
              </button>
            </div>
          </div>
        </form>
      </div>
      <!-- code validation -->
      <div v-else-if="stepState.codeRequired">
        <form @submit.prevent="verify()">
          <p class="lead text-center">
            {{ $t('enrollmentStep.contactVerification.weSentAnEmailTo') }} <span class="block-sm"><strong>{{ stepState.contact }}</strong></span> {{ $t('enrollmentStep.contactVerification.withA6DigitCode') }}

            <br>
            <button class="btn btn-link btn-sm" type="button" @click="updatingContact = true">
              {{ $t('enrollmentStep.contactVerification.updateEmailAddress') }}
            </button>
          </p>

          <div v-if="resentCode" class="text-center alert alert-info alert-dismissible" role="alert">
            <button type="button" class="close" aria-label="Close" @click="resentCode = !resentCode">
              <span aria-hidden="true">×</span>
            </button>
            {{ $t('enrollmentStep.contactVerification.codeSentTo') }} <strong>{{ stepState.contact }}</strong>
          </div>


          <div v-if="submitError" class="text-center alert alert-danger">
            {{ $t('enrollmentStep.contactVerification.couldNotVerifyCode') }}
          </div>

          <template v-if="stepState.hasActiveCode">
            <p class="lead text-center">
              {{ $t('enrollmentStep.contactVerification.enterOrClick') }}
            </p>
            <div class="row justify-content-center">
              <div class="col-md-2 col-sm-4">
                <div class="form-group">
                  <input
                    id="verificationCode"
                    v-model="verificationCode"
                    v-validate.disable="'required|numeric|min:6'"
                    name="verificationCode"
                    class="form-control form-control-lg"
                    style="text-align: center;"
                    maxlength="6"
                    autocomplete="off"
                  >
                </div>
              </div>
            </div>
            <ul v-if="errors.has('verificationCode')" class="text-center text-danger list-unstyled">
              <li>{{ errors.first('verificationCode') }}</li>
            </ul>
            <div class="row text-center">
              <div class="col-md-12">
                <button class="btn btn-primary btn-lg" role="button" type="submit" :disabled="isSubmitting">
                  {{ $t('validate') }}
                </button>
              </div>
            </div>
          </template>
          <div v-else class="alert alert-info text-center">
            {{ $t('enrollmentStep.contactVerification.getNewCode') }}
          </div>
        </form>
        <div class="row text-center">
          <div class="col-md-12">
            <button
              v-if="!resentCode"
              class="btn btn-link"
              type="button"
              data-resend
              :disabled="resentCode || isSubmitting"
              @click="sendNewCode"
            >
              {{ $t('enrollmentStep.contactVerification.resendCode') }}
            </button>
          </div>
        </div>
      </div>
      <!-- soft validation-->
      <div v-else>
        <form @submit.prevent="verify(true)">
          <div v-if="submitError" class="text-center alert alert-danger">
            {{ $t('enrollmentStep.contactVerification.couldNotVerifyCode') }}
          </div>
          <p class="lead text-center">
            {{ $t('enrollmentStep.contactVerification.isEmailCorrect') }}:
          </p>
          <p class="lead text-center">
            <strong>{{ stepState.contact }}</strong>

            <br>
            <button type="button" class="btn btn-link btn-sm" @click="updatingContact = true">
              {{ $t('enrollmentStep.contactVerification.updateEmailAddress') }}
            </button>
          </p>
          <div class="row text-center">
            <div class="col-md-12">
              <button class="btn btn-primary btn-lg" type="submit" :disabled="isSubmitting">
                {{ $t('enrollmentStep.contactVerification.looksGood') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Cell Phone Validation -->
    <div v-if="stepState.method === 'phone'">
      <h1 class="text-center">
        {{ updatingContact ? $t('enrollmentStep.contactVerification.updateYourCell') : $t('enrollmentStep.contactVerification.verifyYourCell') }}
      </h1>
      <!-- missing cell phone || update contact -->
      <div v-if="!stepState.contact || updatingContact">
        <form @submit.prevent="updateContact('cell_phone')">
          <p v-if="!updatingContact" class="lead text-center">
            {{ $t('enrollmentStep.contactVerification.needUpdatedCell') }}
          </p>
          <div class="row justify-content-center">
            <div class="col-md-4">
              <div class="form-group">
                <input
                  v-model="contactValue"
                  v-mask="'(###) ###-####'"
                  v-validate.disable="{required: true, regex: /^\(\d{3}\)\s\d{3}-\d{4}$/}"
                  class="form-control form-control-lg"
                  type="tel"
                  name="cell_phone"
                  autocomplete="off"
                >
              </div>
            </div>
          </div>
          <ul v-if="errors.has('cell_phone')" class="text-center text-danger list-unstyled">
            <li>{{ errors.first('cell_phone') }}</li>
          </ul>
          <div class="row text-center">
            <div class="col-md-12">
              <button class="btn btn-primary btn-lg" type="submit" role="button" :disabled="isSubmitting">
                {{ updatingContact && stepState.codeRequired ? $t('enrollmentStep.contactVerification.updateAndResend') : $t('enrollmentStep.contactVerification.updateCell') }}
              </button>
            </div>
            <div v-if="updatingContact" class="col-md-12">
              <button class="btn btn-link" type="button" @click="cancelUpdate">
                {{ $t('cancel') }}
              </button>
            </div>
          </div>
        </form>
      </div>
      <!-- code validation -->
      <div v-else-if="stepState.codeRequired">
        <form @submit.prevent="verify()">
          <p class="lead text-center">
            {{ $t('enrollmentStep.contactVerification.weSentATextTo') }} <span class="block-sm"><strong>{{ stepState.contact }}</strong></span> {{ $t('enrollmentStep.contactVerification.withA6DigitCode') }}
            <br>
            <button class="btn btn-link btn-sm" type="button" @click="updatingContact = true">
              {{ $t('enrollmentStep.contactVerification.updateCell') }}
            </button>
          </p>

          <div v-if="resentCode" class="text-center alert alert-info alert-dismissible" role="alert">
            <button class="close" type="button" aria-label="Close" @click="resentCode = !resentCode">
              <span aria-hidden="true">×</span>
            </button>
            {{ $t('enrollmentStep.contactVerification.codeSentTo') }} <strong>{{ stepState.contact }}</strong>
          </div>
          <div v-if="submitError" class="text-center alert alert-danger">
            {{ $t('enrollmentStep.contactVerification.couldNotVerifyCode') }}
          </div>

          <template v-if="stepState.hasActiveCode">
            <p class="lead text-center">
              {{ $t('enrollmentStep.contactVerification.pleaseEnterItBelow') }}
            </p>
            <div class="row justify-content-center">
              <div class="col-md-2 col-sm-4">
                <div class="form-group">
                  <input
                    v-model="verificationCode"
                    v-validate.disable="'required|numeric|min:6'"
                    name="verificationCode"
                    class="form-control form-control-lg"
                    style="text-align: center;"
                    maxlength="6"
                    autocomplete="off"
                  >
                </div>
              </div>
            </div>
            <ul v-if="errors.has('verificationCode')" class="text-center text-danger list-unstyled">
              <li>{{ errors.first('verificationCode') }}</li>
            </ul>
            <div class="row text-center">
              <div class="col-md-12">
                <button class="btn btn-primary btn-lg" type="submit" :disabled="isSubmitting" @click="verify()">
                  {{ $t('validate') }}!
                </button>
              </div>
            </div>
          </template>
          <div v-else class="alert alert-info text-center">
            {{ $t('enrollmentStep.contactVerification.getNewCode') }}
          </div>
        </form>
        <div class="row text-center">
          <div class="col-md-12">
            <button
              v-if="!resentCode"
              class="btn btn-link"
              type="button"
              data-resend
              :disabled="resentCode || isSubmitting"
              @click="sendNewCode"
            >
              {{ $t('enrollmentStep.contactVerification.resendCode') }}
            </button>
          </div>
        </div>
      </div>
      <!-- soft validation -->
      <div v-else>
        <form @submit.prevent="verify(true)">
          <div v-if="submitError" class="text-center alert alert-danger">
            {{ $t('enrollmentStep.contactVerification.couldNotVerifyCode') }}
          </div>
          <p class="lead text-center">
            {{ $t('enrollmentStep.contactVerification.verifyPhoneNumber') }}:
          </p>
          <p class="lead text-center">
            <strong>{{ stepState.contact }}</strong>

            <br>
            <button type="button" class="btn btn-link btn-sm" @click="updatingContact = true">
              {{ $t('enrollmentStep.contactVerification.updateCell') }}
            </button>
          </p>

          <div class="row text-center">
            <div class="col-md-12">
              <button
                type="submit"
                class="btn btn-primary btn-lg"
                role="button"
                :disabled="isSubmitting"
                @click="verify(true)"
              >
                {{ $t('enrollmentStep.contactVerification.looksGood') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
  import {mask} from 'vue-the-mask';
  import {Validator} from 'vee-validate';
  import BaseStep from './BaseStep.vue';

  // @TODO: how can I get to these?
  const dict = {
    en: {
      custom: {
        verificationCode: {
          required: 'Cannot be empty.',
          numeric: 'Must be numeric.',
          min: 'Must be 6 characters.'
        },
      },
      attributes: {
        cell_phone: 'Cell phone'
      }
    },
    es: {
      custom: {
        verificationCode: {
          required: 'No puede estar vacío.',
          numeric: 'Debe ser numérico.',
          min: 'Debe ser de 6 caracteres.'
        },
      },
      attributes: {
        cell_phone: 'Teléfono celular'
      }
    }
  };

  Validator.localize(dict);


  export default {
    directives: {
      mask
    },
    extends: BaseStep,
    props: {
      enrollmentState: {
        type: Object,
        default: () => {
          return {};
        }
      }
    },
    data() {
      return {
        isSubmitting: false,
        submitError: false,
        resentCode: false,
        updatingContact: false,
        verificationCode: this.$route.query.code || '',
        contactValue: this.stepState.contact || ''
      };
    },
    methods: {
      sendNewCode() {
        this.submitError = false; // reset alerts
        this.$api.Participants.createNewVerificationCode({
              participant_id: this.participantId
            }
        ).then(({data, status}) => {
          if (status >= 400) {
            //display error?
          }
          this.resentCode = true;
          this.stepState.hasActiveCode = true;
          this.verificationCode = '';
        });// TODO catch
      },
      verify(isSoft) {
        this.$validator.validate().then((result) => {
          // reset alerts
          this.submitError = false;
          this.resentCode = false;
          if (!result) {
            return;
          }
          this.isSubmitting = true;
          var patch = {op: 'verify'};
          if (!isSoft) {
            patch['path'] = '/code';
            patch['value'] = this.verificationCode;
          }
          this.$api.Participants.updateParticipant({
                participant_id: this.participantId,
                patch: [patch]
              }
          ).then(({body, status}) => {
            if (status >= 400) {
              throw {status: status, body: body};
            }

            this.isSubmitting = false;
            this.submitError = false;
            this.$emit('refresh');
          }).catch((error) => {
            const body = error.response.body;
            if (body.field_errors && body.field_errors.length > 0) {
              // the api returned field errors which means the ppt profile failed validation, create an incident
              let msg = ["Participant could not complete contact verification because of an error in their profile:"];
              body.field_errors.forEach((err) => {
                msg.push("Field: " + err.field);
                msg.push("Message: " + err.message);
              });
              this.$api.Participants.createIncidentForParticipant({
                participant_id: this.participantId,
                body: {
                  "type": "Software",
                  "message": msg.join("\n")
                }
              });
            }
            this.isSubmitting = false;
            this.submitError = true;
          });
        });
      },
      updateContact(path) {
        // if the same contact info is used, just resend the code
        if (this.stepState.contact === this.contactValue) {
          this.sendNewCode();
          this.updatingContact = false;
          return;
        }

        this.$validator.validate().then((result) => {
          if (result) {
            this.isSubmitting = true;
            this.$api.Participants.updateParticipant({
                  participant_id: this.participantId,
                  patch: [
                    {op: "replace", path: "/" + path, value: this.contactValue},
                  ]
                }
            ).then(({data, status}) => {
              if (status < 400) {
                if (this.updatingContact) {
                  this.isSubmitting = false;
                  this.updatingContact = false;
                  this.resentCode = true;
                  this.verificationCode = '';
                  this.stepState.hasActiveCode = true;
                  this.stepState.contact = this.contactValue;
                } else {
                  this.$emit('refresh');
                }
              }
            }).catch((error) => {
              if (error.response && error.response.body && error.response.body.field_errors) {
                // todo what about other errors? unique is the only one we rely on backend for
                error.response.body.field_errors.forEach((field_error) => {
                  if (field_error.field == path && field_error.code.includes('duplicate')) {
                    const rule = `unique_${field_error.field}`;
                    const error_key  = `veeValidate.errors.${rule}`;
                    this.$validator.errors.add({
                      field: field_error.field,
                      msg: this.$t(error_key),
                      rule
                    });
                  }
                });
                this.isSubmitting = false;
              }
              this.saving = false;
            });
          }
        });
      },
      cancelUpdate() {
        this.updatingContact = false;
        this.contactValue = this.stepState.contact;
      }
    }
  }
</script>
